import { useBemCN } from '@gik/core/utils/bemBlock';
import DateTimeUtils from '@gik/core/utils/DateTimeUtils';
import { AddInkindUserUpdateButton } from '@gik/inkind-page/components/AddInkindUserUpdateButton/AddInkindUserUpdateButton';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import type InkindUserUpdate from '@gik/inkind-page/models/InkindUserUpdate';
import { LineClamp } from '@gik/ui/LineClamp';
import { AnimatedDivBone } from '@gik/ui/SkeletonLoader';
import { Text } from '@gik/ui/Text/Text';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { fixUserUpdatesLegacyImageUrl } from '../InkindUserUpdateTimelineCard/InkindUserUpdateTimelineCard';

export interface IInkindUserUpdateCardProps {
  update?: InkindUserUpdate;
  canEdit: boolean;
  isLoading?: boolean;
  isPlaceholder?: boolean;
  showAddButton?: boolean;
  addButtonIndex?: number;
  isLoggedIn?: boolean;
  hasUpdates?: boolean;
  openAddUpdateModal?(): void;
  openTimelineModal?(update?: InkindUserUpdate): void;
}

const blockName = 'inkind-user-update-card';

export default function InkindUserUpdateCard({
  update,
  canEdit,
  isPlaceholder,
  isLoading,
  showAddButton,
  addButtonIndex,
  isLoggedIn,
  hasUpdates,
  openAddUpdateModal,
  openTimelineModal,
  children,
}: React.PropsWithChildren<IInkindUserUpdateCardProps>) {
  const bem = useBemCN(blockName);
  const { t } = useTranslation();

  const showUserPlaceholder = React.useMemo(
    () => isLoggedIn && !canEdit && !isLoading && isPlaceholder,
    [isLoading, isLoggedIn, canEdit, isPlaceholder]
  );

  const primaryMedia = React.useMemo(
    () => update?.media.find(m => m.type == 'photo') || update?.media.find(m => m.type == 'video'),
    [update?.media]
  );

  const isOpenable = React.useMemo(() => Boolean(openTimelineModal), [openTimelineModal]);

  const addButton = <AddInkindUserUpdateButton iconOnly={addButtonIndex > 0} onClick={() => openAddUpdateModal?.()} />;

  const userPlaceholder = (
    <>
      <p {...bem('no-update-title')}>
        {t(
          hasUpdates
            ? translationKeys.InkindUserUpdateCardPlaceholderCopy
            : translationKeys.InkindUserUpdateCardPlaceholderCopyNoUpdates
        )}
      </p>
      {!hasUpdates && <p {...bem('subtitle')}>{t(translationKeys.InkindUserUpdateCardPlaceholderSubtitleCopy)}</p>}
    </>
  );

  const content = (
    <div {...bem('content')}>
      {update && (
        <>
          <div {...bem('thumbnail-wrapper')}>
            <AnimatedDivBone {...bem('card-thumbnail-skeleton')} />
            <img
              alt="thumbnail"
              {...bem('card-thumbnail')}
              src={fixUserUpdatesLegacyImageUrl(primaryMedia?.thumbnailUrl)}
            />
          </div>
          <div {...bem('text-wrapper')}>
            <p {...bem('date')}>{moment(update.publishedDate, DateTimeUtils.DateFormat).format('MMM DD, YYYY')}</p>
            <div {...bem('title')}>
              <LineClamp lines={2} fadeOut={false} showButton={false}>
                <p>
                  <Text>{update.title}</Text>
                </p>
              </LineClamp>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      {...bem(null, [
        { 'no-thumbnail': !primaryMedia?.thumbnailUrl },
        { placeholder: isPlaceholder },
        { 'logged-in': showUserPlaceholder },
        { 'add-button': showAddButton },
      ])}
      onClick={() => isOpenable && openTimelineModal(update)}
    >
      {showAddButton || showUserPlaceholder ? (
        <div {...bem('no-updates')}>{showAddButton ? addButton : userPlaceholder}</div>
      ) : (
        children ?? content
      )}
    </div>
  );
}
